// src/components/admin/nornsweave/modals/AddFixedClassModal.js
import React from 'react';
import TimePicker from '../../../TimePicker';
import { GRADE_OPTIONS, SUBJECT_OPTIONS, DAY_OPTIONS, DURATION_OPTIONS } from '../AdminNornsWeaveContext';

function AddFixedClassModal({ fixedClass, setFixedClass, teachers, onClose, onAdd }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Add Fixed Class</h3>
        <div className="space-y-4">
          {/* Teacher Selection from Directory */}
          {teachers.length > 0 && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Select from Directory</label>
              <select
                className="w-full px-3 py-2 border border-emerald-300 rounded-lg bg-emerald-50"
                onChange={(e) => {
                  const teacher = teachers.find(t => t.id === e.target.value);
                  if (teacher) {
                    const gradeMap = { 'PreK': 'PreK', 'K': 'K', '1': '1st', '2': '2nd', '3': '3rd', '4': '4th', '5': '5th', '6': '6th', '7': '7th', '8': '8th' };
                    setFixedClass({
                      ...fixedClass,
                      teacherName: teacher.name,
                      teacherEmail: teacher.email,
                      grade: gradeMap[teacher.grade] || teacher.grade,
                      subject: teacher.subject || ''
                    });
                  }
                }}
              >
                <option value="">-- Quick select teacher --</option>
                {teachers.sort((a, b) => a.name.localeCompare(b.name)).map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} - {t.grade === 'K' ? 'Kindergarten' : t.grade === 'PreK' ? 'PreK' : `${t.grade}th Grade`}{t.subject ? ` (${t.subject})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Teacher Name</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.teacherName}
                onChange={(e) => setFixedClass({...fixedClass, teacherName: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Teacher Email</label>
              <input
                type="email"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.teacherEmail}
                onChange={(e) => setFixedClass({...fixedClass, teacherEmail: e.target.value})}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Grade</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.grade}
                onChange={(e) => setFixedClass({
                  ...fixedClass,
                  grade: e.target.value,
                  subject: ['5th', '6th', '7th', '8th'].includes(e.target.value) ? fixedClass.subject : ''
                })}
              >
                <option value="">Select grade</option>
                {GRADE_OPTIONS.map(g => (
                  <option key={g} value={g}>{g} Grade</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Students</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.studentCount}
                onChange={(e) => setFixedClass({...fixedClass, studentCount: e.target.value})}
              />
            </div>
          </div>
          {['5th', '6th', '7th', '8th'].includes(fixedClass.grade) && (
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Subject</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.subject || ''}
                onChange={(e) => setFixedClass({...fixedClass, subject: e.target.value})}
              >
                <option value="">Select subject</option>
                {SUBJECT_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Day of Week</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={fixedClass.dayOfWeek}
              onChange={(e) => setFixedClass({...fixedClass, dayOfWeek: e.target.value})}
            >
              {DAY_OPTIONS.map(day => (
                <option key={day.value} value={day.value}>{day.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Start Time"
              value={fixedClass.startTime}
              onChange={(val) => setFixedClass({...fixedClass, startTime: val})}
              required
            />
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Duration</label>
              <select
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={fixedClass.duration || ''}
                onChange={(e) => {
                  const duration = parseInt(e.target.value);
                  if (fixedClass.startTime && duration) {
                    const [hours, mins] = fixedClass.startTime.split(':').map(Number);
                    const totalMins = hours * 60 + mins + duration;
                    const endHours = Math.floor(totalMins / 60);
                    const endMins = totalMins % 60;
                    const endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                    setFixedClass({...fixedClass, duration: e.target.value, endTime});
                  } else {
                    setFixedClass({...fixedClass, duration: e.target.value});
                  }
                }}
              >
                <option value="">Select duration</option>
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Library</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={fixedClass.library}
              onChange={(e) => setFixedClass({...fixedClass, library: e.target.value})}
            >
              <option value="elementary">Elementary</option>
              <option value="middle">Middle School</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAdd}
              className="flex-1 bg-cyan-600 text-white py-2 rounded-lg hover:bg-cyan-700"
            >
              Add Fixed Class
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFixedClassModal;
